import {
  Button,
  Card,
  CheckBadgeIcon,
  danger,
  editorConfigOptions,
  ErrorAlert,
  ErrorMessage,
  Header,
  Input,
  JoditEditor,
  LinkBtn,
  PencilSquareIcon,
  PlusCircleIcon,
  success,
  SwitchInput,
  TrashIcon,
  Upload,
  UploadImageCard,
  useCanShow,
  useCustomForm,
  useEffect,
  useFetchData,
  useParams,
  useRef,
  useRemoveItem,
  useUpload,
} from "@";
import {
  getGiftProject,
  saveGiftProject,
  updateGiftProject,
} from "./utils/api";
import { fillForm } from "./utils/helpers";
import GifCardDesign from "./components/GifCardDesign";
import { defaultValues, schema } from "./utils/validation";
import { attributesTableColumns } from "./utils/options";
import { useGifCardDesign, useGiftAttributes } from "./utils/hooks";

const Form = () => {
  const params = useParams();
  const { edit, show } = params;

  const editor = useRef(null);
  const links = [
    { name: "الاهداءات", link: "/gifting" },
    { name: edit ? "تعديل" : show ? "عرض" : "اضافة", link: "" },
  ];

  // Initialize form handling
  const {
    reset,
    watch,
    errors,
    setValue,
    register,
    getValues,
    handleSubmit,
    isSubmitting,
    isSubmitSuccessful,
  } = useCustomForm(defaultValues, schema);

  // Initialize gift attributes handling
  const {
    isEdit,
    addAttribute,
    editAttribute,
    saveEditAttribute,
    cancelEditAttribute,
    resetAttributeFields,
  } = useGiftAttributes(getValues, setValue);

  // Initialize gift card design handling
  const {
    saveGifCardDesign,
    showGifCardDesigner,
    isShowGifCardDesign,
    giftAttributeDesigner,
    setIsShowGifCardDesign,
  } = useGifCardDesign(getValues, setValue);

  const { removeItem } = useRemoveItem(getValues, setValue);
  const { handleUpload } = useUpload(setValue);

  // check if images in getValues("details") all under 100kb
  const images = getValues("details")
    ?.match(/<img[^>]+src="([^"]+)"[^>]*>/g)
    ?.map((img) => img.match(/src="([^"]+)"/)?.[1]);
  const isImagesOverSize = images?.some(
    (img) => img?.length > 1024 * 1024 * 0.2,
  );

  // Fetch data for editing or showing
  const { error, fetchData } = useFetchData({
    reset: reset,
    fill: fillForm,
    id: show || edit,
    fun: getGiftProject,
  });

  // Close gift card designer
  const closeGifCardDesigner = () => {
    document.body.style.overflow = "auto";
    setIsShowGifCardDesign(false);
  };

  // Submit form data
  const submit = async () => {
    try {
      resetAttributeFields();
      const saveFunction = edit ? updateGiftProject : saveGiftProject;
      const { data } = await saveFunction(getValues());
      if (edit) {
        reset(fillForm(data));
        setTimeout(() => window.location.reload(), 1000);
      } else {
        reset();
      }
      await success("تم الحفظ بنجاح");
    } catch (error) {
      danger("حدث خطأ أثناء الحفظ");
    }
  };

  // Show error message if there are form errors
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      danger("يوجد خطأ في البيانات المدخلة");
      console.log("form errors", errors);
    }
  }, [errors]);

  return (
    <>
      {/* Error alert */}
      {(edit || show) && <ErrorAlert reload={fetchData} show={error} />}

      {/* Header */}
      <Header
        flex
        title={`${
          edit ? "تعديل الإهداء" : show ? "عرض الإهداء" : "اضافة إهداء"
        }`}
        links={links}
      >
        {show && useCanShow("editGiftProject") && (
          <LinkBtn to={`/giftProjects/${show}/edit`} edit />
        )}
      </Header>

      {/* Image Design Control */}
      {edit && isShowGifCardDesign && (
        <GifCardDesign
          save={saveGifCardDesign}
          close={closeGifCardDesigner}
          data={giftAttributeDesigner}
          title="تخصيص  بطاقة الاهداء"
        />
      )}

      <form
        onSubmit={handleSubmit(submit)}
        className="relative grid gap-x-5 lg:grid-cols-2"
      >
        {/* Gift Data Section */}
        <Card title="بيانات الاهداء">
          <Input
            style="mb-5"
            disabled={show}
            label="اسم الاهداء"
            error={errors.name}
            input={register("name")}
          />

          <Input
            style="mb-1"
            disabled={show}
            error={errors.title}
            input={register("title")}
            label="العنوان على المنصة"
          />

          {(edit || show) && (
            <a
              rel=""
              target="_blank"
              href={getValues("websiteUrl")}
              className="text-sm text-purple-900 underline-offset-2 hover:underline dark:text-purple-400"
            >
              {getValues("websiteUrl")}
            </a>
          )}

          <Input
            rows={8}
            style="my-5"
            type="textarea"
            disabled={show}
            error={errors.summary}
            label="نبذة عن الاهداء"
            input={register("summary")}
          />
        </Card>

        {/* Gift Settings Section */}
        <Card title="إعدادات الاهداء">
          <div className={`${show ? "flex gap-5" : "mb-0"}`}>
            <UploadImageCard
              show={!show}
              title="صورة الاهداء"
              src={getValues("imageUrl")}
              success={isSubmitSuccessful}
              star={!getValues("imageUrl")}
              error={errors.image?.message}
              upload={(file) => handleUpload(file, "image")}
            >
              <Input
                star="no"
                style="mt-1"
                placeholder=" النص البديل للصورة (alt text)"
                error={errors.imageAlt}
                disabled={show}
                input={register("imageAlt")}
              />
            </UploadImageCard>
          </div>

          {/* Dates and Amounts */}
          <div className="mb-5 grid gap-5 md:grid-cols-2">
            <Input
              star
              disabled={show}
              type="date-range"
              labelA="تاريخ البداية"
              labelB="تاريخ النهاية"
              errorB={errors.endsTo}
              valueB={watch("endsTo")}
              errorA={errors.startsFrom}
              inputB={register("endsTo")}
              valueA={watch("startsFrom")}
              inputA={register("startsFrom")}
              changeB={(e) => setValue("endsTo", e)}
              changeA={(e) => setValue("startsFrom", e)}
            />

            <Input
              star
              type="number"
              disabled={show}
              label="المبلغ المطلوب"
              error={errors.allRequiredAmount}
              input={register("allRequiredAmount")}
            />

            <Input
              star
              type="number"
              disabled={show}
              label="إضافة تبرعات سابقة"
              error={errors.currentPaidAmount}
              input={register("currentPaidAmount")}
            />

            <Input
              star
              min={0}
              type="number"
              label="الترتيب"
              disabled={show}
              error={errors.order}
              input={register("order")}
            />
          </div>

          {/* Visibility Settings */}
          <div className="mb-5 grid gap-5 md:grid-cols-2">
            <SwitchInput
              label=" عرض في الصفحة الرئيسة"
              disabled={show}
              checked={watch("showOnHomePage")}
              change={(value) => setValue("showOnHomePage", value)}
            />

            <SwitchInput
              label=" إخفاء الاهداء من المتجر"
              disabled={show}
              checked={watch("hideFromStore")}
              change={(value) => setValue("hideFromStore", value)}
            />

            <SwitchInput
              label=" تفعيل المؤثر البصري "
              disabled={show}
              checked={watch("giftProjectVisualEffect")}
              change={(value) => setValue("giftProjectVisualEffect", value)}
            />

            <SwitchInput
              label="عرض مؤشر الانجاز"
              disabled={show}
              checked={watch("showProgressBar")}
              change={(value) => setValue("showProgressBar", value)}
            />
          </div>
        </Card>

        {/* Gift Attributes Section */}
        <Card
          star={!show}
          title="فئات الاهداء"
          className="overflow-hidden lg:col-span-2"
        >
          <div className="relative -mx-2 overflow-x-auto md:-mx-4">
            <table className="w-full text-start">
              <thead className="border-t border-gray-100 bg-gray-50 text-sm text-gray-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  {attributesTableColumns.map((item) => (
                    <th scope="col" className="px-6 pb-2 pt-3">
                      {item}
                      {!show && <span className="star">*</span>}
                    </th>
                  ))}
                  {!show && (
                    <th scope="col" className="px-6 pb-2 pt-3">
                      إجراءات
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {!show && (
                  <tr className="rounded-2xl border-b border-gray-100 bg-gray-50 text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400">
                    <td className="px-6 pb-3">
                      <Input
                        star
                        disabled={show}
                        style="min-w-32"
                        placeholder="الاسم"
                        error={errors.attributeTitle}
                        input={register("attributeTitle")}
                      />
                    </td>
                    <td className="px-6 pb-3">
                      <Input
                        star
                        min="1"
                        type="number"
                        disabled={show}
                        style="min-w-32"
                        placeholder="المبلغ"
                        error={errors.attributeAmount}
                        input={register("attributeAmount")}
                      />
                    </td>
                    <td className="px-6 pb-3">
                      <div className="relative">
                        <Upload
                          upload={(e) => {
                            const file = e?.target?.files?.[0];
                            if (!file) return;
                            setValue("attributeImage", {
                              target: { files: [file] },
                            });
                          }}
                          title={
                            watch("attributeImage")?.target?.files?.[0]?.name
                          }
                          className="min-w-40 py-1.5"
                          text={
                            watch("attributeImage")
                              ? watch("attributeImage")?.target?.files?.[0]
                                  ?.name
                              : "رفع صورة"
                          }
                        >
                          {watch("AttributeImage") && (
                            <CheckBadgeIcon className="me-1 h-7 w-7 text-green-500" />
                          )}
                        </Upload>
                      </div>
                    </td>
                    <td className="px-6 pb-3">
                      {isEdit ? (
                        <div className="flex gap-3">
                          <button
                            type="button"
                            className="btn-green"
                            onClick={saveEditAttribute}
                          >
                            حفظ التعديل
                          </button>
                          <button
                            type="button"
                            className="btn-red"
                            onClick={cancelEditAttribute}
                          >
                            الغاء
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="btn-purple-900 inline-flex gap-1 py-2"
                          onClick={addAttribute}
                        >
                          <PlusCircleIcon className="w-6" />
                          اضافة فئة
                        </button>
                      )}
                    </td>
                  </tr>
                )}

                {watch("attributes")?.length > 0 &&
                  watch("attributes").map((item, index) => (
                    <tr
                      className={` ${
                        show
                          ? "border-t even:bg-gray-50 dark:even:bg-gray-700"
                          : "odd:bg-gray-50 dark:odd:bg-gray-700"
                      } rounded-2xl border-b border-gray-100 text-gray-600 last:border-b-0 dark:border-gray-600 dark:text-gray-400`}
                    >
                      <td className="px-6 py-2">{item.title}</td>
                      <td className="px-6 py-2">{item.amount}</td>
                      <td className="px-6 py-2">
                        {(item.imageView || item.imageFullUrl) && (
                          <img
                            alt={item.title}
                            width={64}
                            height={64}
                            onClick={() => showGifCardDesigner(index, edit)}
                            className={`rounded-2xl object-cover ${
                              edit && "cursor-pointer hover:scale-105"
                            }`}
                            src={item.imageView || item.imageFullUrl}
                          />
                        )}

                        {(item.imageView || item.imageFullUrl) && edit && (
                          <p className="mt-1 text-xs">
                            اضغط على الصورة لانشاء الكارت
                          </p>
                        )}
                      </td>
                      {!show && (
                        <td className="px-6 py-2">
                          <span
                            className="icon-style"
                            onClick={() => removeItem("attributes", index)}
                          >
                            <TrashIcon className="w-6" />
                          </span>

                          <span
                            className="icon-style"
                            onClick={() => editAttribute(item, index)}
                          >
                            <PencilSquareIcon className="w-6" />
                          </span>
                        </td>
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <ErrorMessage value={errors.attributes?.message} />
        </Card>

        {/* SEO Settings Section */}
        <Card isOpen={false} className="lg:col-span-2" title=" إعدادات SEO">
          <div className="grid gap-5 lg:grid-cols-3">
            {/* slug */}
            <Input
              star
              label="slug"
              error={errors.slug}
              disabled={show}
              input={register("slug")}
            />

            {/* SEO Title */}
            <Input
              star
              label="SEO Title"
              error={errors.seoTitle}
              disabled={show}
              input={register("seoTitle")}
            />
            {/* SEO Keywords */}
            <Input
              star
              disabled={show}
              error={errors.seoKeywords}
              input={register("seoKeywords")}
              label="SEO Keywords"
            />

            {/* SEO Description */}
            <Input
              star
              style="md:col-span-3"
              type="textarea"
              disabled={show}
              error={errors.seoDescription}
              input={register("seoDescription")}
              label="SEO Description"
            />
          </div>
        </Card>

        {/* Gift Details Section */}
        <Card isOpen={false} title="تفاصيل الاهداء" className="lg:col-span-2">
          {show ? (
            <div dangerouslySetInnerHTML={{ __html: getValues("details") }} />
          ) : (
            <JoditEditor
              ref={editor}
              tabIndex={1}
              value={watch("details")}
              config={editorConfigOptions}
              onChange={(newContent) => setValue("details", newContent)}
            />
          )}
          {isImagesOverSize && (
            <ErrorMessage value="الصور يجب ان تكون اقل من 200 كيلو بايت" />
          )}
          <ErrorMessage value={errors.details?.message} />
        </Card>

        {!show && (
          <div className="floating-btn lg:col-span-2">
            <Button
              type="submit"
              disabled={isImagesOverSize && !edit}
              title="حفظ البيانات"
              isLoading={isSubmitting}
            />
          </div>
        )}
      </form>
    </>
  );
};

export default Form;
